import { Directive } from '@angular/core';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { getYears } from '@df/util/fn/get-years';
import { getMonths } from '@df/util/fn/get-months';
import { CreditCard } from '@df/session/api/credit-cards/credit-card';
import { CreditCards } from '@df/session/api/credit-cards/credit-cards.namespace';
import { AbstractCheckoutPayment } from '@df/checkout/abstract-checkout-payment.component.def';
import { CustomerModel } from '@df/session/api/customer.model';
import { NgInject } from '@df/ng/ng-inject';

@Directive()
export abstract class AbstractCheckoutCreditCardForm<
    T extends Checkout.IPaymentMethod = Checkout.IPaymentMethod,
    D extends Checkout.ICreditCardData = Checkout.ICreditCardData
> extends AbstractCheckoutPayment<T> {
    data: D = {} as D;
    months = getMonths(this.checkoutFormComponent.app.data.locale);
    years = getYears();

    @NgInject(CustomerModel)
    readonly customer: CustomerModel;

    /**
     * Minimum card number length after which we check card type
     * @type {number}
     */
    protected minNumberLength = 3;

    /**
     * Recognized card type
     * @type {string}
     */
    protected _ccType?: string;

    /**
     * Allowed card types options
     * @type {CreditCards.ITypeOption[]}
     */
    protected _cardTypes: CreditCards.ITypeOption[];

    /**
     * Returns allowed card types
     * @returns {string[]}
     */
    protected abstract getAllowedCcTypes(): string[];

    ngOnInit() {
        super.ngOnInit();
        this.subscriptions.add(this.customer.obs.status.subscribe(() => this.detectChanges()));
    }

    /**
     * Card number getter
     * @returns {string}
     */
    get ccNumber(): string {
        return this.data.number;
    }

    /**
     * Card number setter
     */
    set ccNumber(value: string) {
        this.data.number = value;
        this.ccType = this.identifyCcType(this.getCcNumber());
    }

    getCcNumber(data: D = this.data): string {
        return (data.number || '').replace(/\D/g, '');
    }

    /**
     * Current card type getter - recognized automatically or chosen by customer
     * @returns {string | undefined}
     */
    get ccType(): string | undefined {
        return this.getCcType();
    }

    /**
     * Current card type setter
     */
    set ccType(ccType: string | undefined) {
        this._ccType = ccType;
    }

    getCcType(data: D = this.data): string | undefined {
        return this._ccType || this.data.type;
    }

    /**
     * Expiry month getter
     * @returns {number | string}
     */
    get expiryMonth(): number | string {
        return this.getExpiryMonth();
    }

    getExpiryMonth(data: D = this.data): number | string {
        return data.expiryMonth || 0;
    }

    /**
     * Expiry year getter
     * @returns {number | string}
     */
    get expiryYear(): number | string {
        return this.getExpiryYear();
    }

    getExpiryYear(data: D = this.data): number | string {
        return data.expiryYear || 0;
    }

    getCvc(data: D = this.data): string {
        return (data.cvc || '').replace(/\D/g, '');
    }

    /**
     * Checks if credit card was automatically recognized
     * @returns {boolean}
     */
    get cardNotMatched(): boolean {
        const ccNumber = this.getCcNumber();
        if (!ccNumber || ccNumber.length < this.minNumberLength) {
            return false;
        }
        return !this._ccType;
    }

    /**
     * Returns allowe card types options
     * @returns {CreditCards.ITypeOption[]}
     */
    get cardTypes(): CreditCards.ITypeOption[] {
        if (!this._cardTypes) {
            this._cardTypes = CreditCard.getCardTypes(this.getAllowedCcTypes());
        }
        return this._cardTypes;
    }

    /**
     * Track by function for card type select options
     * @param {number} index
     * @param {CreditCards.ITypeOption} cardType
     * @returns {string}
     */
    trackByTypeValue(index: number, cardType: CreditCards.ITypeOption): string {
        return cardType.value;
    }

    /**
     * Returns if we should save card details
     */
    protected storeCard(): boolean {
        return !!(this.customer.isLoggedIn && this.data.store_cc);
    }

    /**
     * Returns last four characters of card number
     * @returns {string}
     */
    protected getLast4(): string {
        return this.getCcNumber().slice(-4);
    }

    /**
     * Tries to recognize card type by card number
     * @param {string} ccNumber
     * @returns {string | undefined}
     */
    protected identifyCcType(ccNumber: string): string | undefined {
        if (!ccNumber || ccNumber.length < this.minNumberLength) {
            return undefined;
        } else {
            const type = CreditCard.matchNumber(ccNumber);
            const allowedTypes = this.getAllowedCcTypes().map(allowedType => allowedType.toLowerCase());

            /**
             * if undefined - returns undefined
             * if no allowedTypes - returns every type
             * if allowedTypes - returns allowed type or undefined
             */
            if (!type || !allowedTypes.length || allowedTypes.indexOf(type.toLowerCase()) > -1) {
                return type;
            } else {
                return undefined;
            }
        }
    }

    /**
     * Returns card holder name
     * @returns {string | undefined}
     */
    protected getCcOwner(data: D = this.data): string | undefined {
        const owner = data.holderName;
        if (!owner) {
            const billingAddress = this.checkoutFormComponent.billingAddress;
            if (billingAddress) {
                return `${billingAddress.firstname} ${billingAddress.lastname}`;
            }
        } else return owner;
    }
}
